import { SecretResponse, SecretScope, SecretType } from '@optidev/shared-types';
import { Eye, EyeOff, MoreVertical, Edit2, Trash2, Copy, Check, Key, Link, Loader2 } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface SecretsTableProps {
  secrets: SecretResponse[];
  mode: 'lite' | 'power';
  scope: SecretScope;
  onEdit: (secret: SecretResponse) => void;
  onDelete: (id: string) => void;
  onUnlink?: (secretId: string) => void;
  loading?: boolean;
}

export default function SecretsTable({
  secrets,
  mode,
  scope,
  onEdit,
  onDelete,
  onUnlink,
  loading = false,
}: SecretsTableProps) {
  const [revealedSecrets, setRevealedSecrets] = useState<Set<string>>(new Set());
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  const toggleReveal = (secretId: string) => {
    const newRevealed = new Set(revealedSecrets);
    if (newRevealed.has(secretId)) {
      newRevealed.delete(secretId);
    } else {
      newRevealed.add(secretId);
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setRevealedSecrets((prev) => {
          const updated = new Set(prev);
          updated.delete(secretId);
          return updated;
        });
      }, 5000);
    }
    setRevealedSecrets(newRevealed);
  };


  const handleCopy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedValue(label);
    toast.success(`Copied to clipboard`);
    setTimeout(() => setCopiedValue(null), 2000);
  };

  const getValueColor = (env: string): string => {
    const colors = {
      base: 'bg-primary/10 border-primary/30 text-primary-700 dark:text-primary-400',
      development: 'bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800/50 text-purple-700 dark:text-purple-300',
      staging: 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800/50 text-orange-700 dark:text-orange-300',
      production: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800/50 text-green-700 dark:text-green-300',
    };
    return colors[env as keyof typeof colors] || colors.base;
  };

  const renderValue = (secret: SecretResponse, env: string = 'base') => {
    const value = secret.values[env as keyof typeof secret.values];
    const isRevealed = revealedSecrets.has(`${secret.id}-${env}`);
    const isSensitive = secret.type === SecretType.SENSITIVE;
    const isVisible = secret.type === SecretType.VISIBLE;

    if (!value) return <span className="text-muted-foreground text-xs italic">Not set</span>;

    const displayValue = isVisible || isRevealed ? value : '••••••••••';

    return (
      <div className={`inline-flex items-center gap-2 px-2 py-1 rounded border ${getValueColor(env)}`}>
        <span className="font-mono text-xs truncate max-w-[200px]" title={isVisible || isRevealed ? value : undefined}>
          {displayValue}
        </span>
        <div className="flex items-center gap-1">
          {!isVisible && !isSensitive && (
            <Button
              onClick={() => toggleReveal(`${secret.id}-${env}`)}
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 hover:bg-transparent"
            >
              {isRevealed ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            </Button>
          )}
          {copiedValue === `${secret.id}-${env}` ? (
            <Check className="h-3 w-3 text-primary rounded-sm" />
          ) : (
            <Button
              onClick={() => handleCopy(value, `${secret.id}-${env}`)}
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 hover:bg-transparent"
            >
              <Copy className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        <span className="text-sm text-muted-foreground">Loading secrets...</span>
      </div>
    );
  }

  if (secrets.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p className="text-sm">No secrets found</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox />
            </TableHead>
            <TableHead>Key</TableHead>
            <TableHead>Type</TableHead>
            {mode === 'lite' ? (
              <TableHead>Value</TableHead>
            ) : (
              <>
                <TableHead>Base</TableHead>
                <TableHead>Development</TableHead>
                <TableHead>Staging</TableHead>
                <TableHead>Production</TableHead>
              </>
            )}
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {secrets.map((secret) => (
            <TableRow key={secret.id} className="group">
              <TableCell>
                <Checkbox />
              </TableCell>
              <TableCell>
                <div className="font-medium">{secret.key}</div>
                {secret.description && (
                  <div className="text-xs text-muted-foreground mt-1">{secret.description}</div>
                )}
              </TableCell>
              <TableCell>
                <div className="flex gap-2 flex-wrap">
                  {secret.type === SecretType.SENSITIVE && (
                    <Badge variant="destructive" className="text-xs">
                      Sensitive
                    </Badge>
                  )}
                  {secret.isLinked ? (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <Link className="h-3 w-3" />
                      Linked
                    </Badge>
                  ) : (
                    <>
                      {scope === SecretScope.ACCOUNT && (
                        <Badge variant="outline" className="text-xs">
                          Account
                        </Badge>
                      )}
                      {scope === SecretScope.PROJECT && (
                        <Badge className="text-xs bg-gray-100 text-primary-700 dark:bg-green-900/30 dark:text-primary-400">
                          Project
                        </Badge>
                      )}
                    </>
                  )}
                </div>
              </TableCell>
              {mode === 'lite' ? (
                <TableCell>{renderValue(secret)}</TableCell>
              ) : (
                <>
                  <TableCell>{renderValue(secret, 'base')}</TableCell>
                  <TableCell>{renderValue(secret, 'development')}</TableCell>
                  <TableCell>{renderValue(secret, 'staging')}</TableCell>
                  <TableCell>{renderValue(secret, 'production')}</TableCell>
                </>
              )}
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {secret.isLinked ? (
                      <>
                        {onUnlink && (
                          <DropdownMenuItem onClick={() => onUnlink(secret.id)}>
                            <Link className="h-4 w-4 mr-2" />
                            Unlink
                          </DropdownMenuItem>
                        )}
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem onClick={() => onEdit(secret)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onDelete(secret.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
